import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, Modal, Input, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { LoginContext } from '../../context/loginContext';

const CPosiciones = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [articulosSeleccionados, setArticulosSeleccionados] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const { user } = useContext(LoginContext);

  useEffect(() => {
    fetchMovimientos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMovimientos = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/movimientos`, { headers });

      // Aplanar info del usuario para facilitar filtros/mostrar
      const transformedData = response.data.map((movimiento) => ({
        ...movimiento,
        usuario: movimiento.idUsuario?.usuario || '',
        cuit: movimiento.idUsuario?.cuit || '',
      }));

      setMovimientos(transformedData);
    } catch (error) {
      message.error('Error al obtener movimientos');
    }
  };

  const handleVerArticulos = (articulos) => {
    setArticulosSeleccionados(articulos || []);
    setIsModalVisible(true);
  };

  const handleModalClose = () => setIsModalVisible(false);

  const limpiarCantidadesResiduales = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      await axios.post(`${process.env.REACT_APP_API_URL}/movimientos/limpiar-residuales`, {}, { headers });
      message.success('Cantidades residuales limpiadas exitosamente');
      fetchMovimientos(); // Recargar los datos
    } catch (error) {
      message.error('Error al limpiar cantidades residuales');
    }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} size="small">
            Buscar
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small">
            Resetear
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered) => <span style={{ color: filtered ? '#1890ff' : undefined }}>🔍</span>,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: '#ffc069', padding: '0 5px' }}>{text}</span>
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Posición',
      dataIndex: 'posicion',
      key: 'posicion',
      ...getColumnSearchProps('posicion'),
    },
    {
      title: 'Cliente',
      dataIndex: 'usuario',
      key: 'usuario',
      ...getColumnSearchProps('usuario'),
    },
    {
      title: 'CUIT',
      dataIndex: 'cuit',
      key: 'cuit',
      ...getColumnSearchProps('cuit'),
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      render: (fecha) => new Date(fecha).toLocaleString(),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button type="primary" onClick={() => handleVerArticulos(record.articulos)}>
          Detalle
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Listado de Posiciones</h2>
        <Button 
          type="default" 
          onClick={limpiarCantidadesResiduales}
          style={{ backgroundColor: '#f0f0f0', borderColor: '#d9d9d9' }}
        >
          Limpiar Cantidades Residuales
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={movimientos}
        rowKey="_id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="Detalles de los Artículos"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Cerrar
          </Button>,
        ]}
        width={900}
      >
        <Table
          dataSource={articulosSeleccionados}
          // En subdocumentos, _id suele existir; si no, generamos un fallback estable
          rowKey={(r) =>
            r._id || `${r?.articulo?._id || 'art'}-${r?.lote || 'nl'}-${r?.fechaVencimiento || 'nf'}`
          }
          pagination={false}
          columns={[
            {
              title: 'Nombre del Artículo',
              dataIndex: ['articulo', 'nombre'],
              key: 'nombre',
            },
            {
              title: 'Marca',
              dataIndex: ['articulo', 'idmarca', 'nombre'],
              key: 'marca',
            },
            {
              title: 'Categoría',
              dataIndex: ['articulo', 'idcategoria', 'nombre'],
              key: 'categoria',
            },
            {
              title: 'Unidad de Medida',
              dataIndex: ['articulo', 'unidadDeMedida'],
              key: 'unidadDeMedida',
            },
            {
              title: 'Cantidad',
              dataIndex: 'cantidad',
              key: 'cantidad',
              render: (cantidad) => Number(cantidad).toFixed(2),
            },
            {
              title: 'Lote',
              dataIndex: 'lote',
              key: 'lote',
              render: (lote) => lote || '—',
            },
            {
              title: 'Fecha de Vencimiento',
              dataIndex: 'fechaVencimiento',
              key: 'fechaVencimiento',
              render: (fv) => (fv ? dayjs(fv).format('YYYY-MM-DD') : '—'),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default CPosiciones;